import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from "react";
import { createPortal } from "react-dom";
import { subscribeVisibleViewport, visibleViewportBand } from "@njbsoft/form-ui";
import { useFocusTrap } from "@/hooks/use-focus-trap";

export interface PopoverProps {
  open: boolean;
  /**
   * The element the overlay measures itself against and returns focus to: the
   * focusable trigger itself, never a wrapper around it — `focus()` on a
   * non-focusable element does nothing, and focus falls to `<body>`.
   */
  anchorRef: RefObject<HTMLElement | null>;
  onClose: () => void;
  /** Rendered on the overlay element, so a shell shortcut can yield to it. */
  role: "dialog" | "menu" | "listbox";
  /** Rendered on the overlay element, so a control outside it can `aria-controls` the popup. */
  id?: string;
  label?: string;
  labelledBy?: string;
  /** Horizontal edge the overlay aligns to; the vertical side flips by room. */
  placement?: "start" | "end";
  /**
   * A viewport point (a right-click's `clientX`/`clientY`) to position off
   * instead of the anchor rect's edges — hanging below-right of it, flipping
   * above and clamping horizontally by the same room math. The anchor element
   * keeps focus-return and outside-click ownership; only the position comes
   * from the point. A point-anchored overlay CLOSES on scroll (a viewport
   * point has no element to re-glue to) and has no width for
   * `width: "anchor"` to copy — it sizes to its content.
   */
  anchorPoint?: { x: number; y: number };
  /** `"anchor"` copies the anchor's width; a number sets it; omitted sizes to the content. */
  width?: number | "anchor";
  trapFocus?: boolean;
  /** Restores focus to the anchor on close. Default `true`. */
  returnFocus?: boolean;
  initialFocusRef?: RefObject<HTMLElement | null>;
  children: ReactNode;
}

/** The height an overlay asks for: under it, the overlay flips above the anchor. */
const POPOVER_MAX_HEIGHT = 240;

/** The floor the room is never squeezed under. */
const POPOVER_MIN_HEIGHT = 96;

/** Breathing room between the anchor and the overlay, and off the viewport edges. */
const POPOVER_GAP = 4;

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not(:disabled)",
  "input:not(:disabled)",
  "select:not(:disabled)",
  "textarea:not(:disabled)",
  '[tabindex]:not([tabindex="-1"])',
].join(", ");

/**
 * The overlay's resolved position. It hangs by its TOP under the anchor, or —
 * when the anchor sits low enough that a full overlay would run past the fold —
 * by its BOTTOM above the anchor. A fixed overlay cannot be scrolled into view,
 * so one opened past the bottom edge is unreachable rather than merely awkward.
 */
interface PopoverRect {
  top?: number;
  bottom?: number;
  left: number;
  width?: number;
  maxHeight: number;
}

type ContainsTarget = (target: Node) => boolean;

/**
 * A nested overlay (a menu's submenu) portals to `document.body` too, so it is
 * no DOM descendant of the overlay that owns it. Each popover registers its
 * own "is this mine" predicate with the popover it renders inside, so a click
 * in a submenu does not read as outside the menu and close it mid-press.
 */
const PopoverNestingContext = createContext<((contains: ContainsTarget) => () => void) | null>(
  null,
);

export function Popover({
  open,
  anchorRef,
  onClose,
  role,
  id,
  label,
  labelledBy,
  placement = "start",
  anchorPoint,
  width,
  trapFocus = false,
  returnFocus = true,
  initialFocusRef,
  children,
}: PopoverProps) {
  const popoverRef = useRef<HTMLDivElement>(null);
  const [rect, setRect] = useState<PopoverRect | null>(null);
  const [shown, setShown] = useState(false);
  const registerWithParent = useContext(PopoverNestingContext);
  const nested = useRef<Set<ContainsTarget>>(new Set());

  useFocusTrap(popoverRef, open && trapFocus);

  const registerNested = useCallback((contains: ContainsTarget) => {
    nested.current.add(contains);
    return () => {
      nested.current.delete(contains);
    };
  }, []);

  const containsTarget = useCallback<ContainsTarget>(
    (target) =>
      (popoverRef.current?.contains(target) ?? false) ||
      [...nested.current].some((contains) => contains(target)),
    [],
  );

  useEffect(() => {
    if (!open || !registerWithParent) return;
    return registerWithParent(containsTarget);
  }, [open, registerWithParent, containsTarget]);

  // Measure the anchor so the portaled overlay can position itself as a fixed
  // box against it, flipping above when the visible band (above the soft
  // keyboard) has no room below and clamping into the viewport horizontally.
  // Recompute on open. A point-anchored overlay runs the same room math against
  // a zero-size rect AT the point, so it hangs below-right of the pointer
  // instead of the anchor's edges.
  const measure = useCallback(() => {
    const anchor = anchorRef.current;
    if (!anchor) return;
    const r = anchorPoint
      ? {
          top: anchorPoint.y,
          bottom: anchorPoint.y,
          left: anchorPoint.x,
          right: anchorPoint.x,
          width: 0,
        }
      : anchor.getBoundingClientRect();
    const band = visibleViewportBand();
    const below = band.bottom - r.bottom - POPOVER_GAP;
    const above = r.top - band.top - POPOVER_GAP;
    const flip = below < POPOVER_MAX_HEIGHT && above > below;
    const room = Math.max(POPOVER_MIN_HEIGHT, flip ? above : below);
    const fixedWidth =
      width === "anchor" && !anchorPoint ? r.width : typeof width === "number" ? width : undefined;
    // A content-sized overlay is already in the DOM by the time this runs, so
    // its own box is what the horizontal clamp has to work from.
    const box = fixedWidth ?? popoverRef.current?.getBoundingClientRect().width ?? 0;
    const wanted = placement === "end" ? r.right - box : r.left;
    const rightmost = Math.max(POPOVER_GAP, window.innerWidth - box - POPOVER_GAP);
    setRect({
      ...(flip
        ? { bottom: window.innerHeight - r.top + POPOVER_GAP }
        : { top: r.bottom + POPOVER_GAP }),
      left: Math.min(Math.max(POPOVER_GAP, wanted), rightmost),
      width: fixedWidth,
      maxHeight: room,
    });
  }, [anchorRef, anchorPoint, placement, width]);

  useLayoutEffect(() => {
    if (open) measure();
  }, [open, measure]);

  // Keep the overlay glued to the anchor while the page or an inner overflow
  // box scrolls. `capture: true` catches scroll on inner boxes (it does not
  // bubble). A point-anchored overlay has nothing to re-glue to — the
  // re-measure would pin it at the captured viewport point while the content
  // scrolls under it — so it CLOSES instead, the way a native context menu
  // does. Resize keeps re-measuring for both anchoring modes (a viewport point
  // does not move on resize).
  useEffect(() => {
    if (!open) return;
    const onScroll = () => {
      if (anchorPoint) {
        onClose();
        return;
      }
      measure();
    };
    const onResize = () => measure();
    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onScroll, true);
    const unsubscribeViewport = subscribeVisibleViewport(onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onScroll, true);
      unsubscribeViewport();
    };
  }, [open, measure, anchorPoint, onClose]);

  useEffect(() => {
    if (!open) {
      setShown(false);
      return;
    }
    const frame = requestAnimationFrame(() => setShown(true));
    return () => cancelAnimationFrame(frame);
  }, [open]);

  // Close on outside click. The portaled overlay lives outside the anchor's
  // container, so a click in either — or in an overlay nested inside this one —
  // is inside.
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (containsTarget(target)) return;
      if (anchorRef.current?.contains(target)) return;
      onClose();
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open, anchorRef, containsTarget, onClose]);

  // The press that lands outside the overlay — on the anchor, on the page —
  // still closes it. Capture, so it beats an enclosing Drawer's document
  // listener; and stopped, so that Drawer does not close on the same press.
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      const target = e.target;
      // An Escape raised inside ANY overlay belongs to the innermost one,
      // which answers it on its own element.
      if (target instanceof Element && target.closest("[data-popover-root]")) return;
      e.preventDefault();
      e.stopPropagation();
      onClose();
    };
    document.addEventListener("keydown", handler, true);
    return () => document.removeEventListener("keydown", handler, true);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return;
    const anchor = anchorRef.current;
    const frame = requestAnimationFrame(() => {
      const el = popoverRef.current;
      if (!el) return;
      const target =
        initialFocusRef?.current ?? el.querySelector<HTMLElement>(FOCUSABLE_SELECTOR) ?? el;
      target.focus();
    });
    return () => {
      cancelAnimationFrame(frame);
      if (returnFocus && anchor && document.contains(anchor)) anchor.focus();
    };
  }, [open, anchorRef, initialFocusRef, returnFocus]);

  if (!open) return null;

  return createPortal(
    <PopoverNestingContext.Provider value={registerNested}>
      <div
        ref={popoverRef}
        data-popover-root=""
        id={id}
        role={role}
        aria-label={label}
        aria-labelledby={labelledBy}
        tabIndex={-1}
        style={{
          top: rect?.top,
          bottom: rect?.bottom,
          left: rect?.left,
          width: rect?.width,
          maxHeight: rect?.maxHeight,
        }}
        className={`fixed z-[60] overflow-y-auto rounded-md border border-gray-200 bg-white shadow-lg outline-none transition-opacity duration-100 motion-reduce:transition-none dark:border-gray-700 dark:bg-gray-800 ${shown ? "opacity-100" : "opacity-0"}`}
        onKeyDown={(e) => {
          if (e.key !== "Escape" || e.defaultPrevented) return;
          e.preventDefault();
          e.stopPropagation();
          onClose();
        }}
      >
        {children}
      </div>
    </PopoverNestingContext.Provider>,
    document.body,
  );
}
